import React from 'react';

import dayjs from 'dayjs';
import { Link } from '~/lib/react-router-dom';

import { Box, Link as MuiLink, Typography } from '@mui/material';

import type { MarketingRouterRuleRecord } from '~/hooks/apis/promo/useMarketingRouterRules';

import { formatMatchValues } from './marketingRouterMatch';

/** Route for a single rule (also its results view). Shared with the timeline. */
export function ruleDetailPath(ruleId: string): string {
  return `/marketing/marketing-router-rules/${encodeURIComponent(ruleId)}`;
}

function formatDay(day: string): string {
  return dayjs(day).format('DD MMM YYYY');
}

type Props = {
  rule: MarketingRouterRuleRecord;
  status: string;
};

/** The hover / focus card for a timeline bar, described by AC10. */
export default function MarketingRouterTimelineTooltip({ rule, status }: Props) {
  const allocation = rule.variants.reduce((total, variant) => total + variant.weight, 0);
  const matchEntries = Object.entries(rule.match).filter(([, value]) => (value ?? []).length > 0);

  return (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="subtitle2">{rule.name}</Typography>
      <Typography variant="caption" component="div">
        {status} · {formatDay(rule.start_date)} → {formatDay(rule.end_date)}
      </Typography>
      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
        {rule.variants.length} {rule.variants.length === 1 ? 'variant' : 'variants'} · {allocation}% allocated
      </Typography>
      {matchEntries.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {matchEntries.map(([field, value]) => (
            <Typography key={field} variant="caption" component="div">
              {field.replace('utm_', 'UTM ')}: {formatMatchValues(value)}
            </Typography>
          ))}
        </Box>
      )}
      {status !== 'Scheduled' && (
        <MuiLink
          component={Link}
          to={ruleDetailPath(rule.id)}
          underline="hover"
          sx={{ mt: 0.5, display: 'inline-block' }}
        >
          View results
        </MuiLink>
      )}
    </Box>
  );
}
